import React from 'react';
import { useForm } from 'react-hook-form';
import { Modal, ModalBody, ModalHeader, Col, Row, ModalFooter, Form } from 'reactstrap';
import Button, { ButtonSizes, ButtonVariants } from '@/components/Button/Button';
import FormInput from '@/components/forms/FormInput';
import { RegionsFilterLocal } from '../regions-query-reducer';

interface RegionsFilterProps {
  onClose: () => void;
  onApplyFilters: (filters: RegionsFilterLocal) => void;
  currentFilters?: RegionsFilterLocal;
}

/**
 * Filter modal for Regions Management page
 * Allows filtering by region name, country name, and various boolean flags
 */
const RegionsFilter: React.FC<RegionsFilterProps> = ({ onClose, onApplyFilters, currentFilters }) => {
  const { control, handleSubmit } = useForm<RegionsFilterLocal>({
    defaultValues: {
      search: currentFilters?.search,
      countryName: currentFilters?.countryName,
      hasSubdivisions: currentFilters?.hasSubdivisions,
      hasAgentDeals: currentFilters?.hasAgentDeals,
    },
  });

  const onSubmit = (data: any) => {
    // Convert string boolean values to actual booleans
    const normalizedData = {
      ...data,
      hasSubdivisions: data.hasSubdivisions === '' ? '' : data.hasSubdivisions === 'true',
      hasAgentDeals: data.hasAgentDeals === '' ? '' : data.hasAgentDeals === 'true',
    };

    // Clean up empty values
    const cleanedFilters = Object.entries(normalizedData).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {} as RegionsFilterLocal);

    onApplyFilters(cleanedFilters);
  };

  return (
    <Modal centered isOpen>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader toggle={onClose} className="d-flex justify-content-between align-items-center w-full">
          Region Filters
        </ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col xs={12}>
              <FormInput name="search" label="Search" placeholder="Search by region name..." control={control} />
            </Col>
            <Col xs={12}>
              <FormInput
                name="countryName"
                label="Country Name"
                placeholder="Filter by country name..."
                control={control}
              />
            </Col>
            <Col xs={12}>
              <FormInput
                name="hasSubdivisions"
                label="Has Subdivisions"
                type="select"
                control={control}
                options={[
                  { value: '', label: 'All' },
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ]}
              />
            </Col>
            <Col xs={12}>
              <FormInput
                name="hasAgentDeals"
                label="Has Agent Deals"
                type="select"
                control={control}
                options={[
                  { value: '', label: 'All' },
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' },
                ]}
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            className="btn btn-outline"
            variant={ButtonVariants.secondary}
            size={ButtonSizes.sm}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-primary"
            variant={ButtonVariants.primary}
            size={ButtonSizes.sm}
            onClick={handleSubmit(onSubmit)}
          >
            Apply Filters
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default RegionsFilter;
